import { useState, useEffect, useCallback, useRef } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cake, Plus, Trash2, Share2, Bell, BellOff, ChevronDown,
  Loader2, CalendarDays, Gift, X, Users, BookUser,
} from "lucide-react";

/**
 * parseVCard — minimal vCard parser. Extracts FN (formatted name) and
 * BDAY (birthday) from a .vcf file. Handles the two common iOS Contacts
 * export shapes:
 *   BDAY:1990-05-15        (full date)
 *   BDAY:--05-15           (year omitted for privacy — we drop it)
 *   BDAY:19900515          (compact ISO)
 * Returns { name, birthday } where birthday is YYYY-MM-DD or "".
 * Year-less birthdays are surfaced as 2000-MM-DD so the date input still
 * accepts them; the user can adjust before saving.
 */
function parseVCard(text) {
  const lines = text.split(/\r?\n/);
  let name = "";
  let birthday = "";
  for (const line of lines) {
    if (!name && /^FN[:;]/i.test(line)) {
      name = line.replace(/^FN[^:]*:/i, "").trim();
    }
    if (!birthday && /^BDAY[:;]/i.test(line)) {
      const raw = line.replace(/^BDAY[^:]*:/i, "").trim();
      // Normalize to YYYY-MM-DD
      const m1 = raw.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
      const m2 = raw.match(/^--(\d{2})-?(\d{2})/);
      if (m1) birthday = `${m1[1]}-${m1[2]}-${m1[3]}`;
      else if (m2) birthday = `2000-${m2[1]}-${m2[2]}`;
    }
    if (name && birthday) break;
  }
  return { name, birthday };
}

/**
 * Schema adapters between the front-end shape this component renders
 * (``{ name, birthday: "YYYY-MM-DD", relationship, days_until, ... }``)
 * and the back-end Friends-Mode contract
 * (``{ friend_name, friend_birthday_month, friend_birthday_day,
 *      friend_birthday_year?, notes?, days_until_birthday, ... }``).
 *
 * Kept as plain functions so they're trivially unit-testable and don't
 * depend on the component instance.
 */
function toBackendFriendPayload({ name, birthday, relationship }) {
  // birthday is the value of an <input type="date">: "YYYY-MM-DD".
  const [y, m, d] = (birthday || "").split("-").map(s => parseInt(s, 10));
  return {
    friend_name: (name || "").trim(),
    friend_birthday_month: m || 1,
    friend_birthday_day: d || 1,
    // Year is optional for the back-end. Drop the 2000-MM-DD sentinel
    // we use when iOS exports a year-less vCard BDAY, so the back-end
    // doesn't think the friend was actually born in 2000.
    friend_birthday_year: (y && y !== 2000) ? y : null,
    notes: (relationship || "").trim() || null,
  };
}

function fromBackendFriend(f) {
  // Reconstruct the YYYY-MM-DD string the renderer expects. If the year
  // was unknown we default to a placeholder year so date math (next
  // upcoming birthday) still works downstream.
  const year = f.friend_birthday_year ?? 2000;
  const mm = String(f.friend_birthday_month || 1).padStart(2, "0");
  const dd = String(f.friend_birthday_day || 1).padStart(2, "0");
  return {
    id: f.id,
    name: f.friend_name || "",
    birthday: `${year}-${mm}-${dd}`,
    relationship: f.notes || "",
    days_until: f.days_until_birthday,
    upcoming_birthday_date: f.upcoming_birthday_date,
    referral_link: f.referral_link || "",
    friend_joined_platform: !!f.friend_joined_platform,
    // Reminder toggle isn't persisted server-side yet — surface as false
    // so the bell icon renders in its "off" state. Toggling it locally
    // remains UI-only until iter316 wires the back-end field.
    remind_7_days: false,
  };
}

const FriendAccordion = ({ friend, expanded, onToggle, onDelete, onToggleRemind, onShare, freebiesData, onLoadFreebies }) => {
  const isUrgent = friend.days_until <= 7;
  const isToday = friend.days_until === 0;
  const bdayDate = friend.birthday ? new Date(friend.birthday + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  useEffect(() => {
    if (expanded && !freebiesData) onLoadFreebies(friend);
  }, [expanded]);

  return (
    <div
      className={`rounded-xl border transition-all overflow-hidden ${
        isToday ? "bg-amber-50 border-amber-200" :
        isUrgent ? "bg-orange-50/50 border-orange-100" :
        "bg-white border-slate-100"
      }`}
      data-testid={`friend-row-${friend.id}`}
    >
      {/* Collapsed header — clickable */}
      <button
        onClick={() => onToggle(friend.id)}
        className="w-full flex items-center gap-3 p-3 text-left"
        data-testid={`friend-toggle-${friend.id}`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isToday ? "bg-amber-200" : isUrgent ? "bg-orange-100" : "bg-slate-100"
        }`}>
          <Cake className={`w-4 h-4 ${isToday ? "text-amber-700" : isUrgent ? "text-orange-500" : "text-slate-400"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-700 truncate">{friend.name}</p>
          <p className="text-[10px] text-slate-400">
            {bdayDate}
            {friend.relationship ? ` · ${friend.relationship}` : ""}
          </p>
        </div>
        <span className={`text-xs font-bold flex-shrink-0 ${isToday ? "text-amber-600" : isUrgent ? "text-orange-500" : "text-slate-400"}`}>
          {isToday ? "Today!" : friend.days_until === 1 ? "Tomorrow" : `${friend.days_until}d`}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-300 flex-shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2.5 border-t border-slate-100/50 pt-2.5">
              {/* Birthday countdown */}
              <div className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2">
                <CalendarDays className="w-4 h-4 text-pink-400 flex-shrink-0" />
                <p className="text-xs text-slate-600">
                  {isToday ? (
                    <span className="font-bold text-amber-600">Birthday is TODAY! Time to celebrate!</span>
                  ) : (
                    <><span className="font-bold">{friend.days_until} days</span> until {friend.name}'s birthday ({bdayDate})</>
                  )}
                </p>
              </div>

              {/* Nearby freebies preview */}
              <div className="bg-white/60 rounded-lg px-3 py-2">
                <p className="text-[10px] font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Gift className="w-3 h-3 text-emerald-500" /> Nearby deals
                </p>
                {freebiesData === null ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-300 mx-auto my-2" />
                ) : freebiesData.length === 0 ? (
                  <p className="text-[10px] text-slate-400">No verified deals found nearby yet.</p>
                ) : (
                  <div className="space-y-1">
                    {freebiesData.slice(0, 3).map((b, i) => (
                      <div key={b.id || i} className="flex items-center gap-1.5 text-[10px]">
                        <span className="text-emerald-400">&#x2022;</span>
                        <span className="text-slate-600 truncate">{b.title}</span>
                      </div>
                    ))}
                    {freebiesData.length > 3 && (
                      <p className="text-[10px] text-slate-400">+{freebiesData.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleRemind(friend); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-semibold transition ${
                    friend.remind_7_days ? "bg-sky-50 text-sky-600 border border-sky-200" : "bg-slate-50 text-slate-400 border border-slate-200"
                  }`}
                  data-testid={`remind-toggle-${friend.id}`}
                >
                  {friend.remind_7_days ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                  {friend.remind_7_days ? "Reminder on" : "Remind me"}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onShare(friend); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 text-[10px] font-semibold transition"
                  data-testid={`share-friend-${friend.id}`}
                >
                  <Share2 className="w-3 h-3" /> Share countdown
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(friend); }}
                  className="p-2 rounded-lg bg-slate-50 text-slate-300 hover:bg-red-50 hover:text-red-500 transition border border-slate-200"
                  data-testid={`delete-friend-${friend.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AddFriendForm = ({ onAdd, onCancel, prefill }) => {
  const [name, setName] = useState(prefill?.name || "");
  const [birthday, setBirthday] = useState(prefill?.birthday || "");
  const [relationship, setRelationship] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-sync when a new contact is picked while the form is already open
  useEffect(() => {
    if (prefill?.name) setName(prefill.name);
    if (prefill?.birthday) setBirthday(prefill.birthday);
  }, [prefill?.name, prefill?.birthday]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !birthday) return;
    setSaving(true);
    await onAdd({ name: name.trim(), birthday, relationship: relationship.trim() });
    setSaving(false);
    setName(""); setBirthday(""); setRelationship("");
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-sky-50/50 rounded-xl border border-sky-100 space-y-3" data-testid="add-friend-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">Add a person</p>
        <button type="button" onClick={onCancel} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-sky-300 focus:ring-1 focus:ring-sky-200 outline-none" data-testid="friend-name-input" required />
      <input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-sky-300 focus:ring-1 focus:ring-sky-200 outline-none" data-testid="friend-birthday-input" required />
      <input type="text" value={relationship} onChange={e => setRelationship(e.target.value)} placeholder="Relationship (optional)" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-sky-300 focus:ring-1 focus:ring-sky-200 outline-none" data-testid="friend-relationship-input" />
      <button type="submit" disabled={saving || !name.trim() || !birthday} className="w-full py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition disabled:opacity-50" data-testid="friend-save-button">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Add"}
      </button>
    </form>
  );
};

export default function FriendsBirthdaySection() {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [freebiesCache, setFreebiesCache] = useState({});
  const [addPrefill, setAddPrefill] = useState(null); // {name, birthday} from Contact Picker / vCard
  const vcardInputRef = useRef(null);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/friends/birthdays`);
      const raw = res.data.friends || [];
      setFriends(raw.map(fromBackendFriend));
    } catch { /* offline / unauthorized — list stays empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFriends(); }, [fetchFriends]);

  const handleAdd = async (data) => {
    try {
      // Translate front-end shape → Friends-Mode back-end contract.
      // ``data`` is { name, birthday: "YYYY-MM-DD", relationship }.
      await axios.post(`${API}/friends/birthdays`, toBackendFriendPayload(data));
      // iter315 + iter316 — Tiered engagement nudges. Fire a one-time
      // celebratory toast each time the user crosses a friendship-count
      // milestone. Each tier persists independently in localStorage so
      // crossing 5 doesn't suppress the 10-friend toast, and refreshing
      // the page doesn't re-fire any tier the user has already seen.
      // ``friends.length`` is the PRE-add count; ``newCount`` is what the
      // server will have after this POST succeeds.
      const newCount = friends.length + 1;
      const MILESTONES = [
        { count: 1,  key: "first_friend_nudge_shown",        msg: "🎉 First friend added — earn 10 pts for every friend whose birthday lands in the app this year." },
        { count: 5,  key: "friend_milestone_5_shown",        msg: "🌟 Birthday Crew unlocked — 50 pts. You're building something special." },
        { count: 10, key: "friend_milestone_10_shown",       msg: "🏆 Birthday Buddy Pro — 100 pts. Your crew's birthdays are fully covered." },
        { count: 25, key: "friend_milestone_25_shown",       msg: "👑 Birthday Legend — 250 pts. You're in rare company." },
      ];
      const fired = MILESTONES.find(m => m.count === newCount && (() => {
        try { return localStorage.getItem(m.key) !== "1"; } catch { return true; }
      })());
      if (fired) {
        toast.success(fired.msg, { duration: 7000, id: `friend-milestone-${fired.count}` });
        try { localStorage.setItem(fired.key, "1"); } catch { /* localStorage unavailable */ }
      } else {
        toast.success(`Added ${data.name}'s birthday`);
      }
      setShowAdd(false);
      setAddPrefill(null);
      fetchFriends();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  // ──────── Contact import ──────────────────────────────────────
  // Path 1 — Chrome Android (Web Contact Picker API).
  // Path 2 — iOS Safari / desktop / browsers without picker support →
  //          hidden <input type="file" accept=".vcf"> fallback so users
  //          can export a contact from their phone's Contacts app and
  //          drop the .vcf into the page.
  const handlePickContact = async () => {
    const supportsPicker = "contacts" in navigator && typeof navigator.contacts.select === "function";
    if (supportsPicker) {
      try {
        const props = ["name"];
        // Only request properties the platform claims to support — passing
        // unsupported props throws on some Android builds.
        let supported = props;
        if (typeof navigator.contacts.getProperties === "function") {
          supported = await navigator.contacts.getProperties();
        }
        const ask = props.filter(p => supported.includes(p));
        if (ask.length === 0) {
          toast.error("Your browser exposes no usable contact fields. Try the .vcf import instead.");
          vcardInputRef.current?.click();
          return;
        }
        const contacts = await navigator.contacts.select(ask, { multiple: false });
        if (!contacts || contacts.length === 0) return; // user cancelled
        const c = contacts[0];
        const picked = Array.isArray(c.name) && c.name.length > 0 ? c.name[0] : "";
        if (!picked) {
          toast.error("That contact had no name we could read.");
          return;
        }
        setAddPrefill({ name: picked, birthday: "" });
        setShowAdd(true);
        if (!sectionOpen) setSectionOpen(true);
        toast.info(`Picked ${picked}. Add their birthday to finish.`);
      } catch {
        // User denied permission, or a transient platform error.
        toast.error("Couldn't open contacts. Try the .vcf import instead.");
        vcardInputRef.current?.click();
      }
    } else {
      // No Contact Picker on this browser → vCard upload fallback.
      vcardInputRef.current?.click();
    }
  };

  const handleVCardFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    try {
      const text = await file.text();
      const { name, birthday } = parseVCard(text);
      if (!name) {
        toast.error("That .vcf had no name we could read.");
        return;
      }
      setAddPrefill({ name, birthday });
      setShowAdd(true);
      if (!sectionOpen) setSectionOpen(true);
      toast.info(
        birthday
          ? `Picked ${name} — birthday pre-filled. Review and save.`
          : `Picked ${name}. Add their birthday to finish.`
      );
    } catch {
      toast.error("Could not read that .vcf file.");
    }
  };

  const handleDelete = async (friend) => {
    if (!window.confirm(`Remove ${friend.name} from your friends?`)) return;
    try {
      await axios.delete(`${API}/friends/birthdays/${friend.id}`);
      toast.success(`Removed ${friend.name}`);
      if (expandedId === friend.id) setExpandedId(null);
      fetchFriends();
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggleRemind = async (friend) => {
    try {
      await axios.put(`${API}/friends/birthdays/${friend.id}`, {
        remind_7_days: !friend.remind_7_days,
      });
      setFriends(prev => prev.map(f =>
        f.id === friend.id ? { ...f, remind_7_days: !f.remind_7_days } : f
      ));
    } catch { toast.error("Failed to update"); }
  };

  const handleShare = async (friend) => {
    const url = `${window.location.origin}/friend-countdown/${friend.share_token}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${friend.name}'s Birthday Countdown`,
          text: `${friend.days_until} days until ${friend.name}'s birthday!`,
          url,
        });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Countdown link copied!");
    }
  };

  const handleLoadFreebies = async (friend) => {
    if (freebiesCache[friend.id]) return;
    try {
      const res = await axios.get(`${API}/friends/birthdays/${friend.id}/freebies`);
      setFreebiesCache(prev => ({ ...prev, [friend.id]: res.data.nearby_freebies || [] }));
    } catch {
      setFreebiesCache(prev => ({ ...prev, [friend.id]: [] }));
    }
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);
  const [sectionOpen, setSectionOpen] = useState(false);
  const upcomingCount = friends.filter(f => f.days_until <= 30).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="friends-birthday-section">
      {/* Section header — clickable accordion toggle */}
      <button
        onClick={() => setSectionOpen(!sectionOpen)}
        className="w-full px-4 pt-4 pb-3 flex items-center justify-between text-left"
        data-testid="friends-section-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
            <Users className="w-4 h-4 text-pink-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">People I Care About</h3>
            <p className="text-[10px] text-slate-400">
              {friends.length > 0
                ? `${friends.length} tracked${upcomingCount > 0 ? ` · ${upcomingCount} upcoming` : ""}`
                : "Track birthdays of friends & family"}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${sectionOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Collapsible content */}
      <AnimatePresence>
        {sectionOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-slate-50 space-y-2">
              {/* Action buttons — manual add OR pick from contacts */}
              <div className="flex justify-end gap-1.5">
                <button
                  onClick={handlePickContact}
                  className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100 transition text-[11px] font-medium"
                  data-testid="add-friend-from-contacts-btn"
                  title="Import from contacts (Android Chrome) or .vcf file"
                >
                  <BookUser className="w-3.5 h-3.5" />
                  From contacts
                </button>
                <button
                  onClick={() => { setAddPrefill(null); setShowAdd(!showAdd); }}
                  className="p-2 rounded-lg bg-sky-50 text-sky-500 hover:bg-sky-100 transition"
                  data-testid="add-friend-btn"
                  title="Add manually"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {/* Hidden vCard input — only used when Contact Picker API is
                  unavailable (iOS Safari, desktop browsers). Triggered
                  programmatically from handlePickContact. */}
              <input
                ref={vcardInputRef}
                type="file"
                accept=".vcf,text/vcard"
                onChange={handleVCardFile}
                className="hidden"
                data-testid="vcard-file-input"
              />

              {showAdd && (
                <AddFriendForm
                  onAdd={handleAdd}
                  onCancel={() => { setShowAdd(false); setAddPrefill(null); }}
                  prefill={addPrefill}
                />
              )}

              {loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
                </div>
              ) : friends.length === 0 ? (
                <div className="text-center py-6" data-testid="friends-empty">
                  <CalendarDays className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No birthdays tracked yet</p>
                  <p className="text-[10px] text-slate-300 mt-1">Tap + to add someone special</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {friends.map(f => (
                    <FriendAccordion
                      key={f.id}
                      friend={f}
                      expanded={expandedId === f.id}
                      onToggle={toggleExpand}
                      onDelete={handleDelete}
                      onToggleRemind={handleToggleRemind}
                      onShare={handleShare}
                      freebiesData={freebiesCache[f.id] ?? null}
                      onLoadFreebies={handleLoadFreebies}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
